import { STATUS_VARIANTS } from '@/components/utils/status-constants';

/** Workbench status filter chips (PipelineStatusChips — same pattern as Task List). */
export const ACCRUALS_ESTIMATES_STATUS_CHIPS = [
  {
    key: 'all',
    label: 'All',
    summaryKey: 'all',
    testId: 'accruals-estimates-page__filter--all',
  },
  {
    key: 'pending_submission',
    label: 'Pending submission',
    summaryKey: 'pending_submission',
    testId: 'accruals-estimates-page__filter--pending_submission',
  },
  {
    key: 'pending_approval',
    label: 'Pending Approval',
    summaryKey: 'pending_approval',
    testId: 'accruals-estimates-page__filter--pending_approval',
  },
  {
    key: 'approved',
    label: 'Approved',
    summaryKey: 'approved',
    testId: 'accruals-estimates-page__filter--approved',
  },
  {
    key: 'rejected',
    label: 'Rejected',
    summaryKey: 'rejected',
    testId: 'accruals-estimates-page__filter--rejected',
  },
  {
    key: 'excluded',
    label: 'Excluded',
    summaryKey: 'excluded',
    testId: 'accruals-estimates-page__filter--excluded',
    dashedBorder: true,
    mutedLabel: true,
  },
];

export const ACCRUALS_ESTIMATE_STATUS_VARIANT = {
  pending_submission: STATUS_VARIANTS.PENDING_SUBMISSION,
  pending_approval: STATUS_VARIANTS.FC_PENDING_APPROVAL,
  approved: STATUS_VARIANTS.FC_APPROVED,
  rejected: STATUS_VARIANTS.FC_REJECTED,
};

export const ACCRUALS_ESTIMATES_TABS = [
  { id: 'estimates', label: 'Workbench' },
  { id: 'schedule', label: 'Reconciliation' },
];

export const ACCRUALS_ESTIMATES_HEADER_MENU_ITEMS = [
  { id: 'configuration', label: 'Configuration', iconKey: 'ICON_FILTER' },
  { id: 'data', label: 'Data', iconKey: 'ICON_DATABASE' },
  { id: 'info-requests', label: 'Info Requests', iconKey: 'ICON_INFO_REQUEST', external: true },
];

export const ACCRUALS_ESTIMATES_TOOLBAR_MENU_ITEMS = [
  { id: 'prepare-schedule', label: 'Prepare Schedule', iconKey: 'ICON_CHECK_WHITE' },
  { id: 'divider' },
  { id: 'rerun-noa', label: 'Re-run Noa', iconKey: 'ESTIMATES_SPARKLE' },
  { id: 'rerun-rules', label: 'Re-run rules', iconKey: 'ICON_RELOAD', emphasis: true },
  { id: 'divider' },
  { id: 'bulk-gl-upload', label: 'Bulk GL upload', iconKey: 'ESTIMATES_UPLOAD' },
  { id: 'sync-info-request', label: 'Sync Info request', iconKey: 'ESTIMATES_SYNC_INFO_REQUEST' },
];

export const ACCRUALS_SCHEDULE_SORT = [
  { id: 'location', label: 'By Location' },
  { id: 'vendor', label: 'By Vendor' },
  { id: 'gl', label: 'By GL' },
];

export const ACCRUALS_SCHEDULE_EMPTY_MESSAGE =
  'No schedule yet. Record estimates, then choose Prepare Schedule.';

export const ACCRUALS_SCHEDULE_MENU_ITEMS = [
  { id: 'preview-export-je', label: 'Preview & Export JE', iconKey: 'ICON_FILE' },
];

export const ACCRUALS_SCHEDULE_TABLE_HEADERS = [
  { text: '', value: 'expand', visible: true, sortable: false, filterable: false },
  { text: 'Location', value: 'location', visible: true, sortable: false, filterable: true },
  { text: 'Vendor', value: 'vendor', visible: true, sortable: false, filterable: true },
  { text: 'GL Account', value: 'glCode', visible: true, sortable: false, filterable: true },
  { text: 'Amount', value: 'totalAmount', visible: true, sortable: false, filterable: false },
];

export const ACCRUALS_RERUN_RULES_DIALOG = {
  heading: 'Re-running will reset affected estimates',
  intro: 'You are initiating a re-run of the accrual estimation model. This operation updates calculations for the active period.',
  impactPrefix: 'Estimates already finalised, and Information Requests already raised, will be ',
  impactHighlight: 'reset for re-evaluation',
  impactSuffix: '.',
  warningTitle: 'Warning: This action cannot be undone',
  warningBody: 'Proceeding will permanently overwrite existing estimates in this cycle with freshly calculated values.',
  cancelLabel: 'Cancel',
  confirmLabel: 'Continue and re-run',
};

export const ACCRUALS_ESTIMATES_FILTERS = [
  { id: 'all', label: 'All', count: 0 },
  { id: 'pending_submission', label: 'Pending submission', count: 0 },
  { id: 'pending_approval', label: 'Pending Approval', count: 0 },
  { id: 'approved', label: 'Approved', count: 0 },
  { id: 'rejected', label: 'Rejected', count: 0 },
  { id: 'excluded', label: 'Excluded', count: 0 },
];

export const ACCRUALS_ESTIMATES_BULK_ACTIONS = [
  { id: 'confirm-selected', label: 'Confirm Selected', iconKey: 'ICON_CHECK_WHITE' },
  { id: 'submit-for-approval', label: 'Submit for approval', iconKey: 'ICON_CHECK_WHITE' },
  { id: 'approve-all', label: 'Approve all', iconKey: 'ICON_CHECK_WHITE' },
  { id: 'exclude', label: 'Exclude', iconKey: 'ICON_CLOSE' },
  { id: 'include', label: 'Include', iconKey: 'ICON_CHECK_WHITE' },
  { id: 'reject', label: 'Reject', iconKey: 'ICON_CLOSE' },
];

export function isReviewerWorkbenchRole(role = '') {
  const normalizedRole = String(role || '').toLowerCase();
  return normalizedRole === ACCRUALS_WORKBENCH_ROLES.APPROVER
    || normalizedRole === ACCRUALS_WORKBENCH_ROLES.MANAGER;
}

export function buildEstimatesBulkActionItems(selectedCount = 0, role = '') {
  const isApprover = isReviewerWorkbenchRole(role);
  const suffix = selectedCount ? ` (${selectedCount})` : '';
  return ACCRUALS_ESTIMATES_BULK_ACTIONS
    .filter((action) => {
      if (isApprover) {
        return ['approve-all', 'reject', 'include'].includes(action.id);
      }
      return ['confirm-selected', 'submit-for-approval', 'exclude', 'include'].includes(action.id);
    })
    .map((action) => {
      if (action.id === 'confirm-selected') {
        return { ...action, label: `${BULK_COPY.CONFIRM_SELECTED}${suffix}` };
      }
      if (action.id === 'submit-for-approval') {
        return { ...action, label: `${BULK_COPY.SUBMIT_FOR_APPROVAL}${suffix}` };
      }
      if (action.id === 'approve-all') {
        return { ...action, label: `${BULK_COPY.APPROVE_ALL}${suffix}` };
      }
      if (action.id === 'exclude') {
        return { ...action, label: BULK_COPY.EXCLUDE };
      }
      if (action.id === 'include') {
        return { ...action, label: BULK_COPY.INCLUDE };
      }
      return { ...action, label: BULK_COPY.REJECT };
    });
}

/**
 * Canonical 5-state workbench lifecycle (frozen contract).
 * pending_submission → pending_approval → approved | rejected
 * pending_submission ↔ excluded · rejected → pending_submission (resubmit)
 * approved → pending_submission (resubmission requested)
 */
export const ACCRUALS_ESTIMATE_STATUS = {
  PENDING_SUBMISSION: 'pending_submission',
  PENDING_APPROVAL: 'pending_approval',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  EXCLUDED: 'excluded',
  // Deprecated read-aliases — do not use in new code. They resolve to the
  // canonical values above so legacy reads keep working.
  NEEDS_REVIEW: 'pending_submission',
  IR_PENDING: 'pending_submission',
  ADD_GL: 'pending_approval',
  RECORDED: 'approved',
};

export const ACCRUALS_ESTIMATE_STATUS_CONFIG = {
  [ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION]: {
    label: 'Pending submission',
    variant: STATUS_VARIANTS.PENDING_SUBMISSION,
  },
  [ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL]: {
    label: 'Pending Approval',
    variant: STATUS_VARIANTS.FC_PENDING_APPROVAL,
  },
  [ACCRUALS_ESTIMATE_STATUS.APPROVED]: {
    label: 'Approved',
    variant: STATUS_VARIANTS.FC_APPROVED,
  },
  [ACCRUALS_ESTIMATE_STATUS.REJECTED]: {
    label: 'Rejected',
    variant: STATUS_VARIANTS.FC_REJECTED,
  },
  [ACCRUALS_ESTIMATE_STATUS.EXCLUDED]: {
    label: 'Excluded',
    variant: STATUS_VARIANTS.DISABLED,
  },
};

export const STATUS_CONFIG = ACCRUALS_ESTIMATE_STATUS_CONFIG;

export const ACCRUALS_WORKBENCH_ROLES = {
  PREPARER: 'preparer',
  APPROVER: 'approver',
  MANAGER: 'manager',
};

export const ACCRUALS_ESTIMATE_TRANSITIONS = {
  [ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION]: [
    ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL,
    ACCRUALS_ESTIMATE_STATUS.EXCLUDED,
  ],
  [ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL]: [
    ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL,
    ACCRUALS_ESTIMATE_STATUS.APPROVED,
    ACCRUALS_ESTIMATE_STATUS.REJECTED,
    ACCRUALS_ESTIMATE_STATUS.EXCLUDED,
  ],
  [ACCRUALS_ESTIMATE_STATUS.APPROVED]: [
    ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION,
  ],
  [ACCRUALS_ESTIMATE_STATUS.REJECTED]: [
    ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION,
    ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL,
    ACCRUALS_ESTIMATE_STATUS.EXCLUDED,
  ],
  [ACCRUALS_ESTIMATE_STATUS.EXCLUDED]: [
    ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION,
  ],
};

const ACCRUALS_TRANSITION_ROLE_ALLOWLIST = {
  [`${ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION}->${ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL}`]: [
    ACCRUALS_WORKBENCH_ROLES.PREPARER,
  ],
  [`${ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL}->${ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL}`]: [
    ACCRUALS_WORKBENCH_ROLES.PREPARER,
  ],
  [`${ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION}->${ACCRUALS_ESTIMATE_STATUS.EXCLUDED}`]: [
    ACCRUALS_WORKBENCH_ROLES.PREPARER,
    ACCRUALS_WORKBENCH_ROLES.APPROVER,
    ACCRUALS_WORKBENCH_ROLES.MANAGER,
  ],
  [`${ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL}->${ACCRUALS_ESTIMATE_STATUS.EXCLUDED}`]: [
    ACCRUALS_WORKBENCH_ROLES.PREPARER,
    ACCRUALS_WORKBENCH_ROLES.APPROVER,
    ACCRUALS_WORKBENCH_ROLES.MANAGER,
  ],
  [`${ACCRUALS_ESTIMATE_STATUS.REJECTED}->${ACCRUALS_ESTIMATE_STATUS.EXCLUDED}`]: [
    ACCRUALS_WORKBENCH_ROLES.PREPARER,
    ACCRUALS_WORKBENCH_ROLES.APPROVER,
    ACCRUALS_WORKBENCH_ROLES.MANAGER,
  ],
  [`${ACCRUALS_ESTIMATE_STATUS.EXCLUDED}->${ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION}`]: [
    ACCRUALS_WORKBENCH_ROLES.PREPARER,
  ],
  [`${ACCRUALS_ESTIMATE_STATUS.REJECTED}->${ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION}`]: [
    ACCRUALS_WORKBENCH_ROLES.PREPARER,
  ],
  [`${ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL}->${ACCRUALS_ESTIMATE_STATUS.APPROVED}`]: [
    ACCRUALS_WORKBENCH_ROLES.APPROVER,
  ],
  [`${ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL}->${ACCRUALS_ESTIMATE_STATUS.REJECTED}`]: [
    ACCRUALS_WORKBENCH_ROLES.APPROVER,
  ],
  [`${ACCRUALS_ESTIMATE_STATUS.APPROVED}->${ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION}`]: [
    ACCRUALS_WORKBENCH_ROLES.APPROVER,
    ACCRUALS_WORKBENCH_ROLES.MANAGER,
  ],
  [`${ACCRUALS_ESTIMATE_STATUS.REJECTED}->${ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL}`]: [
    ACCRUALS_WORKBENCH_ROLES.PREPARER,
  ],
};

export function normalizeEstimateStatus(value) {
  const key = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  switch (key) {
    case 'pending_submission':
    case 'needs_review':
    case 'ir_pending':
      return ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION;
    case 'pending_approval':
    case 'add_gl':
      return ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL;
    case 'approved':
    case 'recorded':
      return ACCRUALS_ESTIMATE_STATUS.APPROVED;
    case 'rejected':
      return ACCRUALS_ESTIMATE_STATUS.REJECTED;
    case 'excluded':
      return ACCRUALS_ESTIMATE_STATUS.EXCLUDED;
    default:
      return ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION;
  }
}

export function canTransition(from, to, role) {
  const origin = normalizeEstimateStatus(from);
  const target = normalizeEstimateStatus(to);
  const allowed = ACCRUALS_ESTIMATE_TRANSITIONS[origin] || [];
  if (!allowed.includes(target)) return false;
  if (!role) return true;
  const permit = ACCRUALS_TRANSITION_ROLE_ALLOWLIST[`${origin}->${target}`];
  if (!permit) return true;
  return permit.includes(String(role).toLowerCase());
}

export function transitionTargetLabel(to) {
  const status = normalizeEstimateStatus(to);
  return ACCRUALS_ESTIMATE_STATUS_CONFIG[status]?.label || status;
}

export function buildLocationMasterMap(locations) {
  const map = new Map();
  (locations || []).forEach((entry) => {
    if (!entry) return;
    const name = String(entry.name || '').trim();
    if (!name) return;
    ['id', 'code', 'subsidiary_code', 'subsidiary', 'name'].forEach((field) => {
      const key = String(entry[field] || '').trim().toLowerCase();
      if (key && !map.has(key)) map.set(key, name);
    });
  });
  return map;
}

export function lookupLocationMasterName(masterMap, row) {
  if (!masterMap || !masterMap.size || !row) return null;
  const raw = row.raw || {};
  const candidates = [
    row.locationKey,
    row.subsidiary_code,
    raw.subsidiary_code,
    raw.location_key,
    raw.location_code,
    raw.code,
    raw.location,
    row.location,
  ];
  for (const candidate of candidates) {
    const key = String(candidate || '').trim().toLowerCase();
    if (!key || key === '—') continue;
    if (masterMap.has(key)) return masterMap.get(key);
  }
  return null;
}

function estimateLocationLooksLikeCode(value) {
  const text = String(value || '').trim();
  if (!text || text === '—') return false;
  return text.includes('_') && !text.includes(' ');
}

/** Human-readable location for UI (table + detail header); matches row mapping + location master. */
export function resolveEstimateDisplayLocation(row, masterMap) {
  if (!row) return '';
  const fromMaster = lookupLocationMasterName(masterMap, row);
  if (fromMaster) return fromMaster;
  const raw = row.raw || {};
  const label = [
    raw.location_label,
    raw.subsidiary_country,
    raw.subsidiary,
  ]
    .map((part) => String(part || '').trim())
    .find((part) => part && part !== '—');
  if (label) return label;
  const rowLocation = String(row.location || '').trim();
  if (rowLocation && rowLocation !== '—' && !estimateLocationLooksLikeCode(rowLocation)) {
    return rowLocation;
  }
  const code = [
    raw.subsidiary_code,
    raw.location,
    rowLocation,
  ]
    .map((part) => String(part || '').trim())
    .find((part) => part && part !== '—');
  if (!code) return '';
  if (masterMap?.size && estimateLocationLooksLikeCode(code)) {
    const key = code.toLowerCase();
    if (masterMap.has(key)) return masterMap.get(key);
  }
  return code;
}

export function applyLocationMasterToRows(rows, masterMap) {
  if (!masterMap || !masterMap.size) return rows || [];
  const applyOne = (row) => {
    if (!row) return row;
    const resolved = lookupLocationMasterName(masterMap, row);
    const lineRows = Array.isArray(row.lineRows)
      ? row.lineRows.map(applyOne)
      : row.lineRows;
    const next = lineRows !== row.lineRows ? { ...row, lineRows } : { ...row };
    if (resolved && resolved !== row.location) next.location = resolved;
    return next.location === row.location && lineRows === row.lineRows ? row : next;
  };
  return (rows || []).map(applyOne);
}

export function lineGlLabel(line, kind) {
  if (!line) return '—';
  const value = kind === 'credit'
    ? (line.creditGl || line.raw?.credit_gl)
    : (line.debitGl || line.raw?.debit_gl);
  const text = String(value || '').trim();
  return text || '—';
}

export function isWorkbenchGlDue(row) {
  if (!row) return false;
  const status = workbenchColumnStatusKey(row);
  if (status === ACCRUALS_ESTIMATE_STATUS.APPROVED) return false;
  if (status === ACCRUALS_ESTIMATE_STATUS.EXCLUDED) return false;
  const confirmed = row.preparerAmount ?? row.raw?.preparer_amount ?? row.postAmount;
  const hasConfirmed = confirmed != null && String(confirmed).trim() !== '';
  if (!hasConfirmed) return false;
  return lineGlLabel(row, 'debit') === '—' || lineGlLabel(row, 'credit') === '—';
}

export const APPROVER_ACTIONS = {
  APPROVE: { id: 'approve', label: 'Approve' },
  APPROVE_ALL: { id: 'approve-all', label: 'Approve all' },
  REJECT: { id: 'reject', label: 'Reject' },
  REQUEST_RESUBMISSION: { id: 'request-resubmission', label: 'Request for resubmission' },
};

export const BULK_COPY = {
  CONFIRM_SELECTED: 'Confirm Selected',
  APPROVE_ALL: 'Approve all',
  SUBMIT_FOR_APPROVAL: 'Submit for approval',
  EXCLUDE: 'Exclude',
  INCLUDE: 'Include',
  REJECT: 'Reject',
};

export const ACCRUALS_APPROVER_BULK_BAR = {
  DESELECT_ALL: 'Deselect all',
  REJECT_SELECTED: 'Reject selected',
  APPROVE_ALL: 'Approve all',
};

export const ACCRUALS_APPROVE_DIALOG = {
  INTRO: 'You are about to approve estimates for the following line items. All selected estimates will be posted to the accruals schedule.',
  NOTE_LABEL: 'Approver note (optional, applies to all)',
  NOTE_PLACEHOLDER: 'Add an optional note for the preparers…',
  CANCEL_LABEL: 'Cancel',
  CONFIRM_LABEL: 'Approve all',
};

export const ACCRUALS_ESTIMATE_ERRORS = {
  CUSTOM_AMOUNT_INVALID: 'Enter a custom amount greater than 0.',
  DEBIT_GL_REQUIRED: 'Select a debit GL account.',
  CREDIT_GL_REQUIRED: 'Select a credit GL account.',
  GL_MUST_DIFFER: 'Debit and credit GL accounts must be different.',
  REJECT_REASON_REQUIRED: 'Add a reason to confirm the rejection.',
  NOTE_TOO_SHORT: 'Enter a note of at least 8 characters.',
  ESTIMATE_REQUIRED: 'Select an estimate.',
  SUBMIT_FAILED: 'Could not submit the estimate. Try again.',
  APPROVE_FAILED: 'Could not approve the estimate. Try again.',
  REJECT_FAILED: 'Could not reject the estimate. Try again.',
  EXCLUDE_FAILED: 'Could not exclude the estimate. Try again.',
  INCLUDE_FAILED: 'Could not include the estimate. Try again.',
  RESUBMIT_FAILED: 'Could not request resubmission. Try again.',
  RECORD_FAILED: 'Could not record the estimate. Try again.',
  LOAD_FAILED: 'Could not load estimates. Try again.',
};

export function validateCustomAmount(value) {
  const amount = parseEstimateMoney(value);
  if (amount == null || !(amount > 0)) return ACCRUALS_ESTIMATE_ERRORS.CUSTOM_AMOUNT_INVALID;
  return '';
}

export function validateRejectReason(value) {
  if (!value || String(value).trim().length < 3) {
    return ACCRUALS_ESTIMATE_ERRORS.REJECT_REASON_REQUIRED;
  }
  return '';
}

export function validatePreparerNote(value) {
  if (!value || String(value).trim().length < 8) {
    return ACCRUALS_ESTIMATE_ERRORS.NOTE_TOO_SHORT;
  }
  return '';
}

export function validateGlPair(debitGl, creditGl) {
  const debit = String(debitGl || '').trim();
  const credit = String(creditGl || '').trim();
  const errors = { debit: '', credit: '', note: '' };
  if (!debit) errors.debit = ACCRUALS_ESTIMATE_ERRORS.DEBIT_GL_REQUIRED;
  if (!credit) errors.credit = ACCRUALS_ESTIMATE_ERRORS.CREDIT_GL_REQUIRED;
  if (debit && credit && debit === credit) {
    errors.note = ACCRUALS_ESTIMATE_ERRORS.GL_MUST_DIFFER;
  }
  return errors;
}

export function estimateRefLabel(row) {
  if (!row) return 'estimate';
  return String(
    row.poNumber
    || row.lineKey
    || row.supplierId
    || row.vendor
    || row.estimateId
    || row._id
    || 'estimate',
  ).trim() || 'estimate';
}

export function buildBulkPartialFailureMessage(failedRefs = []) {
  const refs = (failedRefs || []).map((ref) => String(ref).trim()).filter(Boolean);
  if (!refs.length) return 'Some estimates could not be updated.';
  const shown = refs.slice(0, 5).join(', ');
  const extra = refs.length > 5 ? ` and ${refs.length - 5} more` : '';
  return `Could not update ${refs.length === 1 ? 'this estimate' : 'these estimates'}: ${shown}${extra}. The rest succeeded — try the failed ${refs.length === 1 ? 'one' : 'ones'} again.`;
}

export const ACCRUALS_GL_ASSIGNMENT_COPY = {
  CONFIRMED_PREFIX: 'Estimate confirmed',
  NEXT_STEP: 'GL Assignment',
  RECORD_ENTRY: 'Record',
  RECORD_ENTRY_INVALID: 'Select debit and credit GL accounts on every line before recording the entry.',
  FIELD_REQUIRED: 'Required',
  PLACEHOLDER: 'Select GL account...',
  REFERENCE: 'Reference',
  STATUS: 'Status',
  ESTIMATED_AMOUNT: 'Estimated Amount',
  DEBIT_GL: 'Debit GL Account',
  CREDIT_GL: 'Credit GL Account',
};

export const ACCRUALS_ESTIMATE_INFO_REQUEST = {
  NONE: 'none',
  REPLIED: 'replied',
  NEW_REQUEST: 'new_request',
  FOLLOW_UP: 'follow_up',
};

export const ACCRUALS_ESTIMATE_INFO_REQUEST_LABELS = {
  [ACCRUALS_ESTIMATE_INFO_REQUEST.REPLIED]: 'Replied',
  [ACCRUALS_ESTIMATE_INFO_REQUEST.NEW_REQUEST]: 'New Request',
  [ACCRUALS_ESTIMATE_INFO_REQUEST.FOLLOW_UP]: 'Follow up',
};

export const ACCRUALS_ESTIMATE_NOA_CONFIDENCE = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
};

export const ACCRUALS_ESTIMATE_NOA_CONFIDENCE_CONFIG = {
  [ACCRUALS_ESTIMATE_NOA_CONFIDENCE.HIGH]: {
    label: 'High Confidence',
    modifier: 'high',
  },
  [ACCRUALS_ESTIMATE_NOA_CONFIDENCE.MEDIUM]: {
    label: 'Medium Confidence',
    modifier: 'medium',
  },
  [ACCRUALS_ESTIMATE_NOA_CONFIDENCE.LOW]: {
    label: 'Low Confidence',
    modifier: 'low',
  },
};

export const ACCRUALS_ESTIMATE_DETAIL_PANEL_TABS = [
  { id: 'details', label: 'Details' },
  { id: 'comments', label: 'Comments' },
  { id: 'activity', label: 'Activity' },
];

export const ACCRUALS_COMMENT_TAG = {
  REMARK: 'remark',
  SUBMISSION: 'submission',
  REJECTION: 'rejection',
  RESUBMISSION: 'resubmission',
  APPROVAL: 'approval',
  EXCLUSION: 'exclusion',
};

export const ACCRUALS_COMMENT_TAG_CONFIG = {
  [ACCRUALS_COMMENT_TAG.REMARK]: { label: 'Remark', tone: 'neutral' },
  [ACCRUALS_COMMENT_TAG.SUBMISSION]: { label: 'Submission remark', tone: 'neutral' },
  [ACCRUALS_COMMENT_TAG.REJECTION]: { label: 'Rejection reason', tone: 'danger' },
  [ACCRUALS_COMMENT_TAG.RESUBMISSION]: { label: 'Resubmission remark', tone: 'neutral' },
  [ACCRUALS_COMMENT_TAG.APPROVAL]: { label: 'Approval remark', tone: 'success' },
  [ACCRUALS_COMMENT_TAG.EXCLUSION]: { label: 'Excluded remark', tone: 'neutral' },
};

export const ACCRUALS_ESTIMATE_AMOUNT_OPTION = {
  RULE: 'rule',
  NOA: 'noa',
  CUSTOM: 'custom',
};

function amountSourceDisplayLabel(source) {
  if (source === ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA) return 'Noa';
  if (source === ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM) return 'Custom';
  if (source === ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE) return 'Rule';
  return '';
}

export function estimateAmountSourceLabel(source) {
  return amountSourceDisplayLabel(source) || 'Rule';
}

export const ACCRUALS_RULE_TEMPLATE_LABELS = {
  'AP-01': 'Last Comparable Invoice',
  'AP-02': 'Historical Average',
  'AP-03': 'Post-Cutoff Invoice',
  'PO-01': 'Full Open Balance',
  'PO-02': 'GR Less IR',
  'PO-03': 'Pro-rata (Elapsed Time)',
};

export function getEstimateDetailPanelData(row) {
  if (!row) return null;
  const detail = row.detail || {};
  const raw = row.raw || {};
  const line = (raw.lines || []).find((item) => {
    const key = item?.information_request_id || item?.source_key || item?.po_number;
    return key && String(key) === String(row.lineKey || '');
  });
  const activityDoc = {
    ...raw,
    ...(line || {}),
    approved_by: line?.approved_by || raw.approved_by,
    created_by: line?.created_by || raw.created_by,
  };
  return {
    vendor: row.vendor,
    supplierId: row.supplierId || null,
    poNumber: row.poNumber,
    status: row.status,
    amount: row.amount,
    rule: row.rule,
    winningRuleLabel: formatWinningRuleLabel(activityDoc, row) || row.winningRuleLabel || null,
    ruleEstimate: row.ruleEstimate,
    ruleSection: buildRuleEstimateSection(activityDoc, row),
    activityItems: buildEstimateActivityItems(activityDoc, row),
    noaEstimate: row.noaEstimate,
    noaConfidence: row.noaConfidence,
    infoRequest: row.infoRequest,
    noaQuote: detail.noaQuote || '—',
    noaReasoning: detail.noaReasoning || 'Noa estimate details will appear here.',
    irStatus: detail.irStatus || 'Closed',
    irFromEmail: detail.irFromEmail || '—',
    irQuote: detail.irQuote || '—',
    locked: Boolean(row.locked),
    preparerAmount: row.preparerAmount,
    amountSource: row.amountSource || raw.preparer_amount_source || null,
    debitGl: row.debitGl || raw.debit_gl || null,
    creditGl: row.creditGl || raw.credit_gl || null,
    workbenchStatus: row.workbenchStatus || row.status,
  };
}

function estimateRuleLabel(doc) {
  if (doc.rule_name) return doc.rule_name;
  const code = doc.rule_template_code;
  if (code && ACCRUALS_RULE_TEMPLATE_LABELS[code]) return ACCRUALS_RULE_TEMPLATE_LABELS[code];
  return doc.estimate_method || doc.ai_estimate_method || code || null;
}

export function formatWinningRuleLabel(doc = {}, row = {}) {
  const name = String(doc.rule_name || row.ruleName || estimateRuleLabel(doc) || '').trim();
  const seqRaw = doc.execution_sequence != null ? doc.execution_sequence : row.executionSequence;
  const seqNum = Number(seqRaw);
  const hasSeq = seqRaw != null && seqRaw !== '' && Number.isFinite(seqNum) && seqNum > 0;
  if (hasSeq && name) return `Rule ${seqNum} · ${name}`;
  if (hasSeq) return `Rule ${seqNum}`;
  return name || null;
}

function estimateVendorLabel(doc) {
  return (
    doc.vendor_name
    || doc.supplier_name
    || doc.vendor?.supplier_name
    || doc.supplier_id
    || doc.vendor?.supplier_id
    || '—'
  );
}

function estimatePrimaryAmount(doc, noaAmount) {
  if (doc.preparer_amount != null && doc.preparer_amount !== '') {
    return doc.preparer_amount;
  }
  if (doc.final_accrual_amount != null && doc.final_accrual_amount !== '') {
    return doc.final_accrual_amount;
  }
  if (doc.rule_computed_amount != null && doc.rule_computed_amount !== '') {
    return doc.rule_computed_amount;
  }
  if (doc.open_po_value != null && doc.open_po_value !== '') {
    return doc.open_po_value;
  }
  if (doc.ceiling != null && doc.ceiling !== '') {
    return doc.ceiling;
  }
  return noaAmount;
}

function formatRuleMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  const rounded = Math.round(amount);
  return `$${rounded.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatRuleDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Figma Rule Estimate card (node 335:23379) from computation inputs + estimate identity.
 */
export function buildRuleEstimateSection(doc = {}, row = {}) {
  const amount = doc.rule_computed_amount != null ? doc.rule_computed_amount : row.ruleEstimate;
  if (amount == null || amount === '') return null;
  const inputs = doc.rule_inputs || {};
  const code = doc.rule_template_code || '';
  const label = estimateRuleLabel(doc) || row.ruleName || row.rule || 'Rule estimate';
  const winningRule = formatWinningRuleLabel(doc, row);
  const metrics = [];
  let subtitle;
  const sourceRows = [];

  if (code === 'PO-03') {
    const elapsed = inputs.elapsed_days;
    const totalDays = inputs.total_days;
    const pct = totalDays ? Math.round((Number(elapsed) / Number(totalDays)) * 100) : null;
    subtitle = pct != null
      ? `Pro-rata · ${elapsed} of ${totalDays} days`
      : 'Pro-rata (Elapsed Time)';
    metrics.push(
      { label: 'Elapsed', value: pct != null ? `${elapsed} days · ${pct}%` : `${elapsed || '—'} days` },
      { label: 'Total PO value', value: formatRuleMoney(inputs.total_po_value) },
      { label: 'Service period', value: totalDays != null ? `${totalDays} days` : '—' },
      { label: 'Calculation', value: pct != null ? `${formatRuleMoney(inputs.total_po_value)} × ${pct}%` : '—' },
    );
    sourceRows.push(
      { label: 'PO start', value: formatRuleDate(inputs.service_start_date || doc.service_start_date) },
      { label: 'PO end', value: formatRuleDate(inputs.service_end_date || doc.service_end_date) },
      { label: 'Open PO value', value: formatRuleMoney(doc.open_po_value || inputs.total_po_value) },
    );
  } else if (code === 'PO-02') {
    subtitle = 'GR Less IR';
    metrics.push(
      { label: 'Received', value: formatRuleMoney(inputs.received_to_cutoff || inputs.open_po_value) },
      { label: 'Invoiced', value: formatRuleMoney(inputs.invoiced_to_date) },
      { label: 'Open PO value', value: formatRuleMoney(inputs.open_po_value) },
      { label: 'Calculation', value: 'Received − invoiced' },
    );
    sourceRows.push(
      { label: 'PO number', value: doc.po_number || row.poNumber || '—' },
      { label: 'Open PO value', value: formatRuleMoney(doc.open_po_value) },
    );
  } else if (code === 'PO-01') {
    subtitle = 'Full open balance';
    metrics.push(
      { label: 'Open PO value', value: formatRuleMoney(inputs.open_po_value) },
      { label: 'Currency', value: doc.currency || doc.po_currency || '—' },
      { label: 'PO', value: doc.po_number || row.poNumber || '—' },
      { label: 'Calculation', value: 'Full open PO value' },
    );
    sourceRows.push(
      { label: 'PO number', value: doc.po_number || row.poNumber || '—' },
      { label: 'Open PO value', value: formatRuleMoney(doc.open_po_value || inputs.open_po_value) },
    );
  } else if (code === 'AP-02') {
    const sample = inputs.sample_count;
    subtitle = sample != null ? `Historical average · ${sample} invoices` : 'Historical Average';
    metrics.push(
      { label: 'Sample', value: sample != null ? `${sample} invoices` : '—' },
      { label: 'Average', value: formatRuleMoney(amount) },
      { label: 'Currency', value: doc.currency || doc.transaction_currency || '—' },
      { label: 'Calculation', value: 'Mean of comparable AP invoices' },
    );
    sourceRows.push(
      { label: 'Vendor', value: doc.vendor_name || doc.supplier_name || doc.supplier_id || row.vendor || '—' },
      { label: 'Location', value: doc.subsidiary || doc.subsidiary_code || row.location || '—' },
      { label: 'Template', value: label },
    );
  } else if (code === 'AP-01' || code === 'AP-03') {
    subtitle = label;
    metrics.push(
      { label: 'Invoice amount', value: formatRuleMoney(inputs.invoice_amount || amount) },
      { label: 'Transaction', value: inputs.transaction_id || '—' },
      { label: 'Invoice date', value: formatRuleDate(inputs.transaction_date) },
      { label: 'Currency', value: doc.currency || doc.transaction_currency || '—' },
    );
    sourceRows.push(
      { label: 'Vendor', value: doc.vendor_name || doc.supplier_name || doc.supplier_id || row.vendor || '—' },
      { label: 'Location', value: doc.subsidiary || doc.subsidiary_code || row.location || '—' },
    );
  } else {
    subtitle = label;
    metrics.push(
      { label: 'Amount', value: formatRuleMoney(amount) },
      { label: 'Template', value: code || label },
      { label: 'Currency', value: doc.currency || '—' },
      { label: 'Location', value: doc.subsidiary_code || '—' },
    );
  }

  return {
    amount,
    kicker: 'Rule Estimate',
    ruleLabel: winningRule,
    subtitle,
    metrics: metrics.filter((item) => item.value !== '—' || metrics.length <= 2),
    sourceRows,
  };
}

export const ACCRUALS_ACTIVITY_SYSTEM_USER_ID = '6693c89ee951e60df71f98f1';

export function activityLogMatchesLine(entry, row) {
  if (!entry || !row) return false;
  const lineKey = String(row.lineKey || '').trim();
  const entryKey = String(entry.line_key || entry.meta?.line_key || '').trim();
  if (lineKey && entryKey && (
    entryKey === lineKey
    || lineKey.startsWith(`${entryKey}::`)
    || entryKey.startsWith(`${lineKey}::`)
  )) return true;
  const po = String(row.poNumber || '').trim();
  if (po && String(entry.text || '').includes(po)) return true;
  return false;
}

export function activityUserId(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    const nested = value._id || value.$oid || value.id;
    if (nested != null && nested !== '') return activityUserId(nested);
    const asString = typeof value.toHexString === 'function'
      ? value.toHexString()
      : String(value);
    if (/^[a-fA-F0-9]{24}$/.test(asString)) return asString;
    return '';
  }
  return String(value).trim();
}

export function activityUserDisplayName(user, fallback = '') {
  if (typeof user === 'string' && user.trim()) return user.trim();
  const name = user?.name || user?.displayname || user?.email || user?.contactemail || '';
  if (name) return String(name).trim();
  return String(fallback || '').trim();
}

export function activityInitials(userName) {
  const parts = String(userName || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

export function activityUserEmail(user) {
  if (!user || typeof user !== 'object') return '';
  return String(user.email || user.contactemail || user.useremail || '').trim();
}

export function activityRoleLabel(entry) {
  const stored = String(entry?.actor_role || entry?.actorRole || '').trim();
  if (stored) return stored.charAt(0).toUpperCase() + stored.slice(1).toLowerCase();
  const type = String(entry?.type || '');
  if (['approved', 'bulk_approved', 'rejected'].includes(type)) return 'Approver';
  if (type === 'status_changed' && String(entry?.from || '') === 'approved') return 'Manager';
  if (type === 'status_changed') return 'Approver';
  return 'Preparer';
}

export function activityTimelineSentence(entry, userName) {
  const name = userName || 'Someone';
  const type = String(entry?.type || '');
  if (type === 'status_changed' && String(entry?.from || '') === 'approved') {
    return `${name} requested resubmission of the approved estimate`;
  }
  const sentences = {
    approved: `${name} approved the revised estimate`,
    bulk_approved: `${name} approved the revised estimate`,
    rejected: `${name} rejected the estimate with a correction reason`,
    resubmitted: `${name} resubmitted the estimate with an updated note`,
    submitted: `${name} confirmed the estimate and submitted it for approval`,
    bulk_confirmed: `${name} confirmed the estimate and submitted it for approval`,
    estimate_confirmed: `${name} confirmed the estimate, assigned GLs and submitted`,
    estimate_reconfirmed: `${name} confirmed the estimate and submitted it for approval`,
    excluded: `${name} excluded the line from the cycle`,
    bulk_excluded: `${name} excluded the line from the cycle`,
    reincluded: `${name} included the line back in the cycle`,
    note_added: `${name} added a note`,
    gl_updated: `${name} assigned general ledger accounts`,
    custom_amount_entered: `${name} corrected the custom estimate and resubmitted`,
    estimate_overridden: `${name} changed the estimate`,
  };
  if (sentences[type]) return sentences[type];
  const text = String(entry?.text || '').trim();
  return text || `${name} updated the estimate`;
}

export function activityFiles(entry) {
  const raw = entry?.files || entry?.meta?.files || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((file) => ({
      id: String(file?.id || file?._id || '').trim(),
      name: String(file?.name || 'File').trim() || 'File',
    }))
    .filter((file) => file.id);
}

function withActivityFiles(item, entry) {
  const files = activityFiles(entry);
  return files.length ? { ...item, files } : item;
}

function commentRemarkBody(entry) {
  const direct = String(entry?.comment || entry?.reason || '').trim();
  if (direct) return direct;
  const text = String(entry?.text || '');
  const parts = text.split('—');
  if (parts.length > 1) return parts.slice(1).join('—').trim();
  return '';
}

function commentTagForType(type, entry) {
  if (type === 'rejected' || type === 'bulk_rejected') return ACCRUALS_COMMENT_TAG.REJECTION;
  if (type === 'resubmitted') return ACCRUALS_COMMENT_TAG.RESUBMISSION;
  if (type === 'status_changed' && String(entry?.from || '') === 'approved') {
    return ACCRUALS_COMMENT_TAG.RESUBMISSION;
  }
  if (type === 'approved' || type === 'bulk_approved') return ACCRUALS_COMMENT_TAG.APPROVAL;
  if (type === 'excluded' || type === 'bulk_excluded') return ACCRUALS_COMMENT_TAG.EXCLUSION;
  if (type === 'reincluded') return ACCRUALS_COMMENT_TAG.REMARK;
  if (type === 'submitted' || type === 'bulk_confirmed' || type === 'estimate_confirmed' || type === 'estimate_reconfirmed') {
    return ACCRUALS_COMMENT_TAG.SUBMISSION;
  }
  if (type === 'status_changed') return ACCRUALS_COMMENT_TAG.REMARK;
  return null;
}

const STATUS_COMMENT_TYPES = new Set([
  'submitted',
  'bulk_confirmed',
  'estimate_confirmed',
  'estimate_reconfirmed',
  'resubmitted',
  'approved',
  'bulk_approved',
  'rejected',
  'bulk_rejected',
  'excluded',
  'bulk_excluded',
  'reincluded',
  'status_changed',
]);

function commentSystemLine(entry) {
  const type = String(entry?.type || '');
  const text = String(entry?.text || '').trim();
  if (type === 'status_changed' && String(entry?.from || '') === 'approved') {
    return 'Resubmission requested';
  }
  const lines = {
    submitted: 'Submitted for approval',
    bulk_confirmed: 'Submitted for approval',
    estimate_confirmed: 'Submitted for approval',
    estimate_reconfirmed: 'Submitted for approval',
    resubmitted: 'Submitted for approval',
    approved: 'Approved',
    bulk_approved: 'Approved',
    rejected: 'Rejected',
    excluded: 'Excluded from the cycle',
    bulk_excluded: 'Excluded from the cycle',
    reincluded: 'Included back in the cycle',
    note_added: 'Note added',
    gl_updated: 'General ledger accounts assigned',
    custom_amount_entered: 'Estimate changed',
    estimate_overridden: 'Estimate changed',
    status_changed: 'Status updated',
  };
  return lines[type] || text || 'Update';
}

/**
 * Turn activity rows into the Comments thread: a remark when the actor left
 * text, and a system line for every preparer or approver action.
 * @param {object[]} entries Shaped activity entries, oldest first
 * @returns {object[]} Comment feed items
 */
export function buildCommentFeed(entries) {
  const items = [];
  (entries || []).forEach((entry) => {
    const type = String(entry?.type || '');
    const text = String(entry?.text || '').trim();
    const body = commentRemarkBody(entry);
    const ruleEstimate = !type && /rule estimate/i.test(text);
    const statusChange = STATUS_COMMENT_TYPES.has(type);
    const estimateChange = type === 'estimate_overridden' || type === 'custom_amount_entered';
    if (estimateChange) {
      const detail = String(entry.detail || '').trim();
      const systemText = detail || text || commentSystemLine(entry);
      items.push({
        kind: 'system',
        id: `${entry.id}-system`,
        text: systemText,
        time: entry.commentTime || entry.timestamp || '',
      });
      if (body) {
        items.push(withActivityFiles({
          kind: 'comment',
          id: `${entry.id}-comment`,
          name: entry.userName || 'Unknown',
          initials: entry.initials || '?',
          time: entry.commentTime || entry.timestamp || '',
          tag: null,
          body,
        }, entry));
      }
      return;
    }
    if (body || ruleEstimate || statusChange) {
      items.push(withActivityFiles({
        kind: 'comment',
        id: `${entry.id}-comment`,
        name: entry.userName || 'Unknown',
        initials: entry.initials || '?',
        time: entry.commentTime || entry.timestamp || '',
        tag: body && statusChange ? commentTagForType(type, entry) : null,
        body: body || text || commentSystemLine(entry),
      }, entry));
      return;
    }
    if (text) {
      items.push(withActivityFiles({
        kind: 'comment',
        id: `${entry.id}-comment`,
        name: entry.userName || 'Unknown',
        initials: entry.initials || '?',
        time: entry.commentTime || entry.timestamp || '',
        tag: null,
        body: text,
      }, entry));
    }
  });
  return items;
}

export function buildEstimateActivityItems(doc = {}, row = {}) {
  const items = [];
  const created = doc.created_at || doc.updated_at;
  const createdBy = activityUserId(
    doc.im_requested_by || doc.created_by || row.imRequestedBy,
  );
  if (created && createdBy) {
    items.push({
      id: 'created',
      text: 'Estimate created',
      at: created,
      userId: createdBy,
    });
  }
  const ruleBy = activityUserId(doc.rule_computed_by || row.ruleComputedBy);
  if (doc.rule_computed_amount != null && ruleBy) {
    items.push({
      id: 'rule',
      text: `Rule estimate calculated${row.rule ? ` · ${row.rule}` : ''}`,
      at: doc.rule_computed_at || doc.updated_at,
      userId: ruleBy,
    });
  }
  if (doc.ai_evaluated_at && ruleBy) {
    items.push({
      id: 'noa',
      text: 'Noa estimate updated',
      at: doc.ai_evaluated_at,
      userId: ruleBy,
    });
  }
  const imBy = activityUserId(
    doc.im_responded_by || doc.im_info_owner_id || doc.im_requested_by || row.imRequestedBy,
  );
  if (doc.im_responded_at && imBy) {
    items.push({
      id: 'im',
      text: 'Information request replied',
      at: doc.im_responded_at,
      userId: imBy,
    });
  }
  const unlockedBy = activityUserId(doc.unlocked_by || row.unlockedBy);
  if (doc.unlocked_at && unlockedBy) {
    items.push({
      id: 'unlocked',
      text: 'Estimate unlocked',
      at: doc.unlocked_at,
      userId: unlockedBy,
    });
  }
  const confirmedBy = activityUserId(
    doc.confirmed_by || row.confirmedBy || doc.updated_by || doc.approved_by,
  );
  if (doc.confirmed_at && confirmedBy) {
    const confirmedAmount = doc.preparer_amount ?? row.preparerAmount;
    const sourceLabel = amountSourceDisplayLabel(
      doc.preparer_amount_source || row.amountSource,
    );
    const note = String(doc.preparer_note || doc.note_to_approver || row.noteToApprover || '').trim();
    items.push({
      id: 'confirmed',
      type: 'estimate_confirmed',
      comment: note,
      text: confirmedAmount != null && confirmedAmount !== ''
        ? `Estimate confirmed · ${formatEstimateMoney(confirmedAmount)}${sourceLabel ? ` via ${sourceLabel}` : ''}`
        : 'Estimate confirmed — GL assignment pending',
      at: doc.confirmed_at,
      userId: confirmedBy,
    });
  } else if (normalizeEstimateStatus(doc.workbench_status) === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL
    && doc.locked && !doc.approved_at && confirmedBy) {
    const note = String(doc.preparer_note || doc.note_to_approver || row.noteToApprover || '').trim();
    items.push({
      id: 'confirmed',
      type: 'estimate_confirmed',
      comment: note,
      text: 'Estimate confirmed — GL assignment pending',
      at: doc.updated_at,
      userId: confirmedBy,
    });
  }
  const recordedBy = activityUserId(doc.approved_by || row.approvedBy || doc.confirmed_by);
  if ((doc.approved_at || doc.finalized_at
    || normalizeEstimateStatus(doc.workbench_status) === ACCRUALS_ESTIMATE_STATUS.APPROVED)
    && recordedBy) {
    const debit = doc.debit_gl || row.debitGl;
    const credit = doc.credit_gl || row.creditGl;
    const recordedAmount = doc.final_accrual_amount ?? doc.preparer_amount ?? row.preparerAmount;
    const sourceLabel = amountSourceDisplayLabel(
      doc.preparer_amount_source || row.amountSource,
    );
    items.push({
      id: 'recorded',
      type: 'approved',
      comment: String(doc.approver_note || row.approverNote || '').trim(),
      text: recordedAmount != null && recordedAmount !== ''
        ? `Estimate recorded · ${formatEstimateMoney(recordedAmount)}`
        : 'Estimate recorded',
      at: doc.approved_at || doc.finalized_at || doc.updated_at,
      detail: [
        sourceLabel ? `Confirmed via ${sourceLabel}` : '',
        [debit, credit].filter(Boolean).length
          ? `Debit GL ${debit || '—'} · Credit GL ${credit || '—'}`
          : '',
      ].filter(Boolean).join(' · '),
      userId: recordedBy,
    });
  }
  const workflowEvents = Array.isArray(doc.workflow_events) ? doc.workflow_events : [];
  const eventTypes = new Set(workflowEvents.map((entry) => String(entry?.type || '')));
  workflowEvents.forEach((entry, index) => {
    const comment = String(entry?.comment || entry?.text || '').trim();
    const type = String(entry?.type || 'status_changed');
    if (!comment && !type) return;
    items.push({
      id: `workflow-event-${index}`,
      type,
      comment,
      text: comment || type,
      at: entry.at || entry.created_at || doc.updated_at,
      userId: activityUserId(entry.by || entry.userid),
      userName: activityUserDisplayName(entry.user || entry.userid_detail),
      userRecord: entry.user || entry.userid_detail || null,
      from: entry.from || '',
    });
  });
  const excludeReason = String(doc.exclude_reason || row.excludeReason || '').trim();
  if (excludeReason && !eventTypes.has('excluded') && !eventTypes.has('bulk_excluded')) {
    items.push({
      id: 'excluded',
      type: 'excluded',
      comment: excludeReason,
      text: `Excluded from the cycle — ${excludeReason}`,
      at: doc.excluded_at || doc.updated_at,
      userId: activityUserId(doc.excluded_by || row.excludedBy),
      userName: activityUserDisplayName(doc.excluded_by_detail),
      userRecord: doc.excluded_by_detail || null,
    });
  }
  const includeReason = String(doc.include_reason || row.includeReason || '').trim();
  if (includeReason && !eventTypes.has('reincluded')) {
    items.push({
      id: 'reincluded',
      type: 'reincluded',
      comment: includeReason,
      text: `Included back in the cycle — ${includeReason}`,
      at: doc.reincluded_at || doc.updated_at,
      userId: activityUserId(doc.reincluded_by || row.reincludedBy),
      userName: activityUserDisplayName(doc.reincluded_by_detail),
      userRecord: doc.reincluded_by_detail || null,
    });
  }
  (Array.isArray(doc.comments) ? doc.comments : []).forEach((entry, index) => {
    const text = String(entry?.text || entry?.note || '').trim();
    if (!text) return;
    items.push({
      id: `saved-comment-${index}`,
      type: 'note_added',
      comment: text,
      text,
      at: entry.at || entry.created_at || doc.updated_at,
      userId: activityUserId(entry.by || entry.userid || entry.created_by),
    });
  });
  (doc.activity || []).forEach((entry, index) => {
    if (!entry?.text) return;
    items.push({
      id: `log-${index}`,
      text: entry.text,
      at: entry.created_at || entry.at,
      userId: activityUserId(entry.userid || entry.user_id || entry.created_by),
      userName: activityUserDisplayName(entry.userName || entry.userid_detail),
    });
  });
  return items
    .filter((item) => item.at)
    .sort((a, b) => new Date(a.at) - new Date(b.at));
}

/**
 * Map GET /v2/financialclose/accruals/estimates row → AccrualsEstimates table row.
 */
function resolveDocWorkbenchStatus(doc, { finalized, submitted, irPending }) {
  const rawCandidates = [
    doc.workbench_status,
    doc.status,
    doc.review_status,
  ].map((value) => String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_'))
    .filter(Boolean);
  const known = new Set([
    'pending_submission', 'needs_review', 'ir_pending',
    'pending_approval', 'add_gl',
    'approved', 'recorded',
    'rejected', 'excluded',
  ]);
  const explicit = rawCandidates.find((value) => known.has(value));
  if (explicit) return normalizeEstimateStatus(explicit);
  if (doc.excluded === true || doc.is_excluded === true) {
    return ACCRUALS_ESTIMATE_STATUS.EXCLUDED;
  }
  if (doc.rejected_at || doc.rejected_by) return ACCRUALS_ESTIMATE_STATUS.REJECTED;
  if (finalized) return ACCRUALS_ESTIMATE_STATUS.APPROVED;
  if (submitted) return ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL;
  if (irPending) return ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION;
  return ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION;
}

function mapEstimateFields(doc, { idSuffix } = {}) {
  if (!doc) return null;
  const confidence = (doc.confidence || doc.ai_confidence || '').toString().toLowerCase();
  const imStage = doc.im_stage != null ? Number(doc.im_stage) : doc.stage != null ? Number(doc.stage) : null;
  const reviewStatus = (doc.review_status || '').toString();
  const finalized = Boolean(
    doc.approved_at
    || doc.finalized_at
    || doc.workbench_status === 'recorded'
    || reviewStatus === 'Finalized'
  );
  const addGl = !finalized && (
    doc.workbench_status === 'add_gl'
    || reviewStatus === 'Add GL'
    || reviewStatus === ACCRUALS_ESTIMATE_STATUS.ADD_GL
    || Boolean(doc.locked)
  );
  const noaStatus = (doc.noa_status || '').toString();
  const estimationStatus = (doc.estimation_status || '').toString();
  const submitted = Boolean(
    doc.submitted_at
    || doc.confirmed_at
    || addGl
    || normalizeEstimateStatus(doc.workbench_status) === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL,
  );
  const irPending = noaStatus === 'ir_pending'
    || (!finalized && !addGl && (
      imStage === 0
      || (reviewStatus === 'IR Pending' && (imStage == null || imStage < 1))
    ));

  const status = resolveDocWorkbenchStatus(doc, { finalized, submitted, irPending });

  const filterTags = [status];

  let infoRequest = ACCRUALS_ESTIMATE_INFO_REQUEST.NONE;
  if (doc.information_request_id) {
    if (imStage === 0 || irPending) infoRequest = ACCRUALS_ESTIMATE_INFO_REQUEST.NEW_REQUEST;
    else if (imStage >= 1) infoRequest = ACCRUALS_ESTIMATE_INFO_REQUEST.REPLIED;
    else infoRequest = ACCRUALS_ESTIMATE_INFO_REQUEST.NEW_REQUEST;
  }

  const textSignal = doc.signals?.text || {};
  const noaAmount =
    doc.ai_estimated_amount != null ? doc.ai_estimated_amount : doc.estimated_amount;
  const ruleLabel = estimateRuleLabel(doc);
  const winningRuleLabel = formatWinningRuleLabel(doc);
  const quote =
    doc.im_response
    || textSignal.reasoning
    || doc.ai_reasoning
    || doc.reasoning
    || null;

  const irStageLabel =
    imStage === 0
      ? 'Pending'
      : imStage === 1
        ? 'Submitted'
        : imStage === 2
          ? 'Approved'
          : imStage === -1
            ? 'Cancelled'
            : reviewStatus || '—';

  const baseId = doc._id?.$oid || doc._id?.toString?.() || doc._id;
  const irId = doc.information_request_id?.$oid
    || doc.information_request_id?.toString?.()
    || doc.information_request_id
    || null;
  const po = String(doc.po_number || doc.document_number || '').trim();
  const poLine = String(doc.po_line_number || '').trim();
  let lineKey = null;
  if (irId) lineKey = String(irId);
  else if (po) lineKey = `${po}::${poLine}`;
  else if (doc.source_key) lineKey = String(doc.source_key);
  return {
    _id: idSuffix ? `${baseId}:${idSuffix}` : baseId,
    estimateId: baseId,
    lineKey,
    locked: finalized || addGl || Boolean(doc.locked),
    supplierId: doc.supplier_id || doc.vendor?.supplier_id || null,
    vendor: estimateVendorLabel(doc),
    poNumber: doc.po_number || doc.document_number || null,
    poLineNumber: doc.po_line_number || null,
    location:
      doc.location_label ||
      doc.subsidiary_country ||
      doc.subsidiary ||
      doc.subsidiary_code ||
      doc.location ||
      '—',
    locationKey: doc.location_key != null ? String(doc.location_key) : (doc.subsidiary_code || ''),
    status,
    workbenchStatus: status,
    noaStatus: irPending
      ? 'ir_pending'
      : (noaStatus || (imStage >= 1 && (doc.ai_estimated_amount != null || doc.estimated_amount != null)
        ? 'ready'
        : noaStatus || 'none')),
    estimationStatus: estimationStatus === 'active' || doc.rule_computed_amount != null
      ? 'active'
      : 'idle',
    ruleComputedBy: doc.rule_computed_by || null,
    imRequestedBy: doc.im_requested_by || null,
    confirmedBy: doc.confirmed_by || null,
    unlockedBy: doc.unlocked_by || null,
    approvedBy: doc.approved_by || null,
    reviewStatus,
    imStage,
    amount: estimatePrimaryAmount(doc, noaAmount),
    postAmount: (finalized || addGl)
      ? (doc.final_accrual_amount != null ? doc.final_accrual_amount : doc.preparer_amount)
      : null,
    debitGl: doc.debit_gl || doc.default_gl?.debit_gl || null,
    creditGl: doc.credit_gl || doc.default_gl?.credit_gl || null,
    imOwnerEmail: doc.im_info_owner_email || doc.detail?.irFromEmail || null,
    imSentAt: doc.im_sent_at || doc.im_requested_at || doc.created_at || null,
    currency: doc.currency || doc.po_currency || doc.transaction_currency || null,
    description: doc.description || doc.item_description || doc.po_description || null,
    openPoValue: doc.open_po_value != null ? doc.open_po_value : null,
    rule: winningRuleLabel || ruleLabel,
    ruleName: doc.rule_name || null,
    executionSequence: doc.execution_sequence != null ? doc.execution_sequence : null,
    winningRuleLabel,
    ruleEstimate: doc.rule_computed_amount != null ? doc.rule_computed_amount : null,
    noaEstimate: noaAmount,
    noaConfidence: confidence || null,
    preparerAmount: doc.preparer_amount != null ? doc.preparer_amount : null,
    amountSource: doc.preparer_amount_source || null,
    approverNote: doc.approver_note || null,
    infoRequest,
    informationRequestId: doc.information_request_id?.$oid
      || doc.information_request_id?.toString?.()
      || doc.information_request_id
      || null,
    filterTags,
    raw: doc,
    detail: {
      noaQuote: quote || '—',
      noaReasoning: doc.ai_reasoning || doc.reasoning || '—',
      irStatus: irStageLabel,
      irFromEmail: doc.im_info_owner_email || '—',
      irQuote: doc.im_response || textSignal.reasoning || '—',
    },
  };
}

export function mapEstimateApiToRow(doc) {
  if (!doc) return null;
  const lines = Array.isArray(doc.lines) ? doc.lines : [];
  const vendorDoc = {
    ...doc,
    po_number: null,
    document_number: null,
    po_line_number: null,
    information_request_id: null,
    im_stage: null,
    stage: null,
    location_key: doc.location_key,
    location_label: doc.location_label,
  };
  const vendorRow = mapEstimateFields(vendorDoc);
  if (!vendorRow) return null;
  vendorRow.lineRows = lines.map((line, index) =>
    mapEstimateFields({
      ...line,
      _id: line.information_request_id || line.source_key || `${vendorRow._id}:line:${index}`,
      supplier_id: doc.supplier_id,
      supplier_name: doc.supplier_name,
      vendor_name: doc.vendor_name,
      vendor: doc.vendor,
      subsidiary_country: line.subsidiary_country || line.location || doc.subsidiary_country,
      subsidiary: line.subsidiary || doc.subsidiary,
      subsidiary_code: line.subsidiary_code || doc.subsidiary_code,
      location_key: line.location_key != null ? line.location_key : (line.subsidiary_code || doc.location_key),
      location: line.location
        || line.subsidiary
        || line.subsidiary_country
        || line.subsidiary_code
        || doc.location_label
        || doc.location,
    })
  ).filter(Boolean).map((row) => ({
    ...row,
    estimateId: vendorRow.estimateId || vendorRow._id,
  }));
  return vendorRow;
}

export function buildEstimatesFilters(counts = {}) {
  return ACCRUALS_ESTIMATES_FILTERS.map((f) => ({
    ...f,
    count: Number(counts[f.id] != null ? counts[f.id] : 0),
  }));
}

export const ACCRUALS_ESTIMATES_TABLE_HEADERS = [
  { text: 'Vendor', value: 'vendor', visible: true, sortable: false, filterable: true },
  { text: 'Location', value: 'location', visible: true, sortable: false, filterable: true },
  { text: 'Estimated Amount', value: 'postAmount', visible: true, sortable: false, filterable: false, headerClass: 'accruals-estimates-table__amount-heading' },
];

export const ACCRUALS_ESTIMATE_SOURCE_LABELS = {
  [ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE]: 'Rule',
  [ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA]: 'Noa',
  [ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM]: 'Custom',
};

export const ACCRUALS_ESTIMATE_ROW_MENU_ITEMS = [
  { id: 'view-details', label: 'View details' },
  { id: 'submit-for-approval', label: 'Submit for approval' },
  { id: 'approve', label: 'Approve' },
  { id: 'reject', label: 'Reject' },
  { id: 'request-resubmission', label: 'Request for resubmission' },
  { id: 'exclude-from-cycle', label: 'Exclude from cycle' },
  { id: 'include-in-cycle', label: 'Include in cycle' },
  { id: 'view-info-request', label: 'View info request', iconKey: 'ICON_INFO_REQUEST', external: true },
];

export const ACCRUALS_ESTIMATES_MOCK_ROWS = [
  {
    _id: 'est-001',
    vendor: 'Acme Freight',
    poNumber: 'PO-4472',
    location: 'United States',
    status: ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION,
    amount: 24500,
    rule: null,
    noaEstimate: 13900,
    noaConfidence: ACCRUALS_ESTIMATE_NOA_CONFIDENCE.HIGH,
    infoRequest: ACCRUALS_ESTIMATE_INFO_REQUEST.REPLIED,
    filterTags: ['pending_submission'],
    detail: {
      noaQuote: '95% of service received',
      noaReasoning: 'Owner confirmed 22 days delivered; basis adjusted upward from straight-line pro-rata. Noa recommends $13,900.',
      irStatus: 'Closed',
      irFromEmail: '[email protected]',
      irQuote: '95% of service received — 22 of 30 days delivered as of 31 Mar.',
    },
  },
  {
    _id: 'est-002',
    vendor: 'Vendor Name',
    poNumber: 'PO-3318',
    location: 'United Kingdom',
    status: ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION,
    amount: 18200,
    rule: null,
    noaEstimate: 13900,
    noaConfidence: null,
    infoRequest: ACCRUALS_ESTIMATE_INFO_REQUEST.NONE,
    filterTags: ['pending_submission'],
  },
  {
    _id: 'est-003',
    vendor: 'Global Logistics Co.',
    poNumber: 'PO-2291',
    location: 'Germany',
    status: ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL,
    amount: 31400,
    rule: null,
    noaEstimate: null,
    noaConfidence: null,
    infoRequest: ACCRUALS_ESTIMATE_INFO_REQUEST.FOLLOW_UP,
    filterTags: [],
  },
  {
    _id: 'est-004',
    vendor: 'Summit Supplies',
    poNumber: 'PO-1187',
    location: 'Canada',
    status: ACCRUALS_ESTIMATE_STATUS.APPROVED,
    amount: 9800,
    rule: null,
    noaEstimate: 9800,
    noaConfidence: null,
    infoRequest: ACCRUALS_ESTIMATE_INFO_REQUEST.NEW_REQUEST,
    filterTags: [],
  },
  {
    _id: 'est-005',
    vendor: 'Pacific Materials',
    poNumber: 'PO-5520',
    location: 'Australia',
    status: ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION,
    amount: 42100,
    rule: null,
    noaEstimate: 38500,
    noaConfidence: ACCRUALS_ESTIMATE_NOA_CONFIDENCE.HIGH,
    infoRequest: ACCRUALS_ESTIMATE_INFO_REQUEST.NONE,
    filterTags: ['pending_submission'],
  },
  {
    _id: 'est-006',
    vendor: 'Northern Equipment',
    poNumber: 'PO-7743',
    location: 'Norway',
    status: ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION,
    amount: 15600,
    rule: null,
    noaEstimate: 13900,
    noaConfidence: ACCRUALS_ESTIMATE_NOA_CONFIDENCE.HIGH,
    infoRequest: ACCRUALS_ESTIMATE_INFO_REQUEST.FOLLOW_UP,
    filterTags: ['pending_submission'],
  },
  {
    _id: 'est-007',
    vendor: 'Atlas Consulting',
    poNumber: 'PO-6612',
    location: 'France',
    status: ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION,
    amount: 27300,
    rule: null,
    noaEstimate: null,
    noaConfidence: ACCRUALS_ESTIMATE_NOA_CONFIDENCE.MEDIUM,
    infoRequest: ACCRUALS_ESTIMATE_INFO_REQUEST.NEW_REQUEST,
    filterTags: ['pending_submission'],
  },
  {
    _id: 'est-008',
    vendor: 'Blue Ridge Services',
    poNumber: 'PO-9034',
    location: 'United States',
    status: ACCRUALS_ESTIMATE_STATUS.APPROVED,
    amount: 11200,
    rule: null,
    noaEstimate: 11200,
    noaConfidence: null,
    infoRequest: ACCRUALS_ESTIMATE_INFO_REQUEST.REPLIED,
    filterTags: [],
  },
  {
    _id: 'est-009',
    vendor: 'Horizon Partners',
    poNumber: 'PO-4455',
    location: 'India',
    status: ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL,
    amount: 19800,
    rule: null,
    noaEstimate: 17500,
    noaConfidence: null,
    infoRequest: ACCRUALS_ESTIMATE_INFO_REQUEST.NONE,
    filterTags: [],
  },
  {
    _id: 'est-010',
    vendor: 'Sterling Imports',
    poNumber: 'PO-2876',
    location: 'Japan',
    status: ACCRUALS_ESTIMATE_STATUS.REJECTED,
    amount: 33600,
    rule: null,
    noaEstimate: 31000,
    noaConfidence: ACCRUALS_ESTIMATE_NOA_CONFIDENCE.LOW,
    infoRequest: ACCRUALS_ESTIMATE_INFO_REQUEST.FOLLOW_UP,
    filterTags: ['rejected'],
  },
  {
    _id: 'est-011',
    vendor: 'Vertex Solutions',
    poNumber: 'PO-1590',
    location: 'Singapore',
    status: ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION,
    amount: 22400,
    rule: null,
    noaEstimate: 20100,
    noaConfidence: ACCRUALS_ESTIMATE_NOA_CONFIDENCE.HIGH,
    infoRequest: ACCRUALS_ESTIMATE_INFO_REQUEST.REPLIED,
    filterTags: ['pending_submission'],
  },
  {
    _id: 'est-012',
    vendor: 'Crown Industries',
    poNumber: 'PO-8201',
    location: 'Netherlands',
    status: ACCRUALS_ESTIMATE_STATUS.EXCLUDED,
    amount: 14700,
    rule: null,
    noaEstimate: 14700,
    noaConfidence: null,
    infoRequest: ACCRUALS_ESTIMATE_INFO_REQUEST.NONE,
    filterTags: ['excluded'],
  },
];

export function filterEstimatesRows(rows, filterId) {
  if (!filterId || filterId === 'all') return rows;
  const status = normalizeEstimateStatus(filterId);
  return rows.filter((row) => (
    (row.filterTags || []).includes(filterId)
    || (row.filterTags || []).includes(status)
    || normalizeEstimateStatus(row.workbenchStatus ?? row.status) === status
  ));
}

export function isVendorLevelEstimateRow(row) {
  if (!row) return false;
  if (row.poNumber) return false;
  if (Array.isArray(row.lineRows) && row.lineRows.length) return false;
  if (row.raw?.source === 'vendor_rule') return true;
  if (row.raw?.estimate_kind === 'vendor' && row.isGroup) return false;
  return row.ruleEstimate != null || Boolean(row.raw?.rule_template_code);
}

function vendorGroupKey(row) {
  const id = String(row?.supplierId || '').trim();
  const name = String(row?.vendor || '').trim().toLowerCase();
  return id || name || 'unknown';
}

function locationGroupKey(row) {
  if (row?.locationKey != null && String(row.locationKey).trim() !== '') {
    return String(row.locationKey).trim();
  }
  const code = String(row?.raw?.subsidiary_code || '').trim();
  if (code) return code;
  const label = String(row?.location || '').trim();
  return label === '—' ? '' : label;
}

export function isPoIrPending(row) {
  if (!row || isVendorLevelEstimateRow(row)) return false;
  if (row.noaStatus === 'ir_pending') return true;
  return row.infoRequest === ACCRUALS_ESTIMATE_INFO_REQUEST.NEW_REQUEST
    || row.infoRequest === ACCRUALS_ESTIMATE_INFO_REQUEST.FOLLOW_UP;
}

export function isPoNoaReady(row) {
  if (!row || isVendorLevelEstimateRow(row) || isPoIrPending(row)) return false;
  if (row.infoRequest === ACCRUALS_ESTIMATE_INFO_REQUEST.REPLIED) return true;
  const status = workbenchColumnStatusKey(row);
  if (status === ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION
    || status === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL) {
    return row.noaEstimate != null || Boolean(row.informationRequestId);
  }
  return row.noaEstimate != null && row.noaEstimate !== '';
}

export function poNoaDisplayState(row) {
  if (isPoIrPending(row)) return 'ir';
  if (isPoNoaReady(row)) return 'ready';
  return 'idle';
}

/**
 * Status key for the Noa Est. column badge only.
 * Uses the IR/Noa lifecycle — not the workbench status.
 */
export function noaColumnStatusKey(line) {
  if (!isPoIrPending(line)) return null;
  return ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION;
}

/**
 * Status key for the expand-panel workbench Status column only.
 * Always a canonical 5-state value; never uses the IR lifecycle.
 */
export function workbenchColumnStatusKey(line) {
  return normalizeEstimateStatus(line?.workbenchStatus ?? line?.status);
}

export function isConfirmedWorkbenchRow(row) {
  const status = workbenchColumnStatusKey(row);
  return status === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL
    || status === ACCRUALS_ESTIMATE_STATUS.APPROVED;
}

export function isSubmittedWorkbenchRow(row) {
  return workbenchColumnStatusKey(row) === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL;
}

export function groupWorkbenchStatuses(group) {
  const statuses = new Set();
  (group?.children || []).forEach((row) => {
    if (!row || isVendorLevelEstimateRow(row)) return;
    statuses.add(workbenchColumnStatusKey(row));
  });
  return statuses;
}

export function isPoRuleEstimate(row) {
  if (!row || isVendorLevelEstimateRow(row)) return false;
  return row.ruleEstimate != null && row.ruleEstimate !== '';
}

function sumPresentField(rows, field, { confirmedOnly = false } = {}) {
  let total = 0;
  let hasValue = false;
  (rows || []).forEach((row) => {
    if (confirmedOnly && !isConfirmedWorkbenchRow(row)) return;
    if (row?.[field] == null || row[field] === '') return;
    hasValue = true;
    total += Number(row[field]) || 0;
  });
  return hasValue ? total : null;
}

/**
 * Parent Rule Est. shows the location sum only after PO lines are Add GL or Recorded.
 * Expand-panel Rule Est. still shows a calculated amount in Needs Review.
 */
export function visibleParentRuleEstimate(group) {
  return sumPresentField(group?.children, 'ruleEstimate', { confirmedOnly: true });
}

export function visibleParentNoaEstimate(group) {
  return sumPresentField(group?.children, 'noaEstimate');
}

export function visibleLineRuleEstimate(line) {
  const amount = line?.ruleEstimate;
  return amount == null || amount === '' ? null : amount;
}

export function visibleLineNoaEstimate(line) {
  const amount = line?.noaEstimate;
  return amount == null || amount === '' ? null : amount;
}

function sumChildNoaEstimates(rows) {
  let total = 0;
  let hasValue = false;
  (rows || []).forEach((row) => {
    if (isVendorLevelEstimateRow(row)) return;
    if (row?.noaEstimate == null || row.noaEstimate === '') return;
    hasValue = true;
    total += Number(row.noaEstimate) || 0;
  });
  return hasValue ? total : null;
}

function uniqueLocations(rows) {
  const values = [];
  (rows || []).forEach((row) => {
    const location = String(row.location || '').trim();
    if (!location || location === '—') return;
    if (!values.includes(location)) values.push(location);
  });
  if (!values.length) return '—';
  return values[0];
}

function sortVendorChildren(rows) {
  return [...(rows || [])].sort((a, b) => {
    const aRule = isVendorLevelEstimateRow(a) ? 0 : 1;
    const bRule = isVendorLevelEstimateRow(b) ? 0 : 1;
    if (aRule !== bRule) return aRule - bRule;
    const aPending = a.noaStatus === 'ir_pending' ? 1 : 0;
    const bPending = b.noaStatus === 'ir_pending' ? 1 : 0;
    if (aPending !== bPending) return aPending - bPending;
    return String(a.poNumber || '').localeCompare(String(b.poNumber || ''));
  });
}

function groupInfoRequest(children) {
  if ((children || []).some((row) => row.infoRequest === ACCRUALS_ESTIMATE_INFO_REQUEST.REPLIED)) {
    return ACCRUALS_ESTIMATE_INFO_REQUEST.REPLIED;
  }
  if ((children || []).some((row) => row.infoRequest === ACCRUALS_ESTIMATE_INFO_REQUEST.FOLLOW_UP)) {
    return ACCRUALS_ESTIMATE_INFO_REQUEST.FOLLOW_UP;
  }
  if ((children || []).some((row) => row.infoRequest === ACCRUALS_ESTIMATE_INFO_REQUEST.NEW_REQUEST)) {
    return ACCRUALS_ESTIMATE_INFO_REQUEST.NEW_REQUEST;
  }
  return ACCRUALS_ESTIMATE_INFO_REQUEST.NONE;
}

function visibleChildrenForFilter(children, filterId) {
  if (!filterId || filterId === 'all') return children;
  const status = normalizeEstimateStatus(filterId);
  return (children || []).filter((row) => {
    if (isVendorLevelEstimateRow(row)) return true;
    return workbenchColumnStatusKey(row) === status
      || (row.filterTags || []).includes(filterId);
  });
}

function groupMatchesFilter(group, filterId) {
  if (!filterId || filterId === 'all') return true;
  const status = normalizeEstimateStatus(filterId);
  if ((group.filterTags || []).includes(filterId)) return true;
  return groupWorkbenchStatuses(group).has(status);
}

/**
 * One expandable parent per vendor + location. Status lives on PO children only.
 */
export function groupEstimatesByVendor(rows, filterId = 'all') {
  const buckets = new Map();
  const pushChild = (supplierKey, locationKey, child) => {
    const key = `${supplierKey}::${locationKey}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(child);
  };
  (rows || []).forEach((row) => {
    const supplierKey = vendorGroupKey(row);
    const lineRows = Array.isArray(row.lineRows) ? row.lineRows : [];
    if (lineRows.length || row.raw?.estimate_kind === 'vendor' || row.raw?.lines) {
      lineRows.forEach((line) => {
        pushChild(supplierKey, locationGroupKey(line) || locationGroupKey(row), line);
      });
      if (!lineRows.length) pushChild(supplierKey, locationGroupKey(row), row);
      return;
    }
    pushChild(supplierKey, locationGroupKey(row), row);
  });

  const groups = [];
  buckets.forEach((children, key) => {
    const visible = visibleChildrenForFilter(sortVendorChildren(children), filterId);
    if (!visible.length) return;
    const vendorRuleRow = visible.find(isVendorLevelEstimateRow) || null;
    const poRows = visible.filter((row) => !isVendorLevelEstimateRow(row));
    const named = visible.find((row) => {
      const name = String(row.vendor || '').trim();
      return name && name !== '—' && name !== row.supplierId;
    });
    const vendorName = named?.vendor || vendorRuleRow?.vendor || visible[0]?.vendor || key;
    const location = uniqueLocations(visible);
    const locationKey = locationGroupKey(visible[0]) || '';
    const ruleEstimate = visible.reduce((sum, row) => (
      row.ruleEstimate != null ? sum + (Number(row.ruleEstimate) || 0) : sum
    ), 0);
    const hasRule = visible.some((row) => row.ruleEstimate != null);
    const postAmount = visibleParentFinalAmount({ children: visible });
    const group = {
      _id: `vendor:${key}`,
      isGroup: true,
      supplierId: visible[0]?.supplierId || null,
      vendor: vendorName,
      poNumber: null,
      poCount: poRows.filter((row) => row.poNumber).length,
      childCount: visible.length,
      location,
      locationKey,
      status: null,
      preparerAmount: vendorRuleRow?.preparerAmount ?? null,
      postAmount,
      rule: vendorRuleRow?.rule || null,
      ruleEstimate: hasRule ? ruleEstimate : null,
      noaEstimate: sumChildNoaEstimates(visible),
      noaConfidence: visible.find((row) => row.noaConfidence)?.noaConfidence || null,
      debitGl: vendorRuleRow?.debitGl || visible.find((row) => row.debitGl)?.debitGl || null,
      creditGl: vendorRuleRow?.creditGl || visible.find((row) => row.creditGl)?.creditGl || null,
      imOwnerEmail: visible.find((row) => row.imOwnerEmail)?.imOwnerEmail
        || visible.find((row) => row.detail?.irFromEmail && row.detail.irFromEmail !== '—')?.detail?.irFromEmail
        || null,
      imSentAt: visible.find((row) => row.imSentAt)?.imSentAt || null,
      informationRequestId: visible.find((row) => row.informationRequestId)?.informationRequestId || null,
      locked: visible.some((row) => row.locked),
      infoRequest: groupInfoRequest(visible),
      vendorRuleRow,
      children: visible,
      filterTags: [...new Set(visible.flatMap((row) => row.filterTags || []))],
    };
    group.refTags = buildVendorRefTags(group);
    group.estimatedAccrual = groupEstimatedAccrual(group);
    group.poLines = noaPoLines(group);
    if (!groupMatchesFilter(group, filterId)) return;
    groups.push(group);
  });

  return groups.sort((a, b) => {
    const vendorCmp = String(a.vendor).localeCompare(String(b.vendor));
    if (vendorCmp !== 0) return vendorCmp;
    return String(a.location).localeCompare(String(b.location));
  });
}

export function findEstimateLineInRows(rows, selected) {
  if (!selected) return null;
  const estimateId = String(selected.estimateId || selected.raw?._id || selected._id || '');
  const lineKey = String(selected.lineKey || '');
  const selectedId = String(selected._id || '');
  for (const row of rows || []) {
    const lines = Array.isArray(row.lineRows) ? row.lineRows : [];
    const match = lines.find((line) => {
      const lineEstimateId = String(line.estimateId || row.estimateId || row._id || '');
      if (estimateId && lineEstimateId !== estimateId) return false;
      if (lineKey) return String(line.lineKey || '') === lineKey;
      return String(line._id || '') === selectedId;
    });
    if (match) return match;
    if (!lineKey && estimateId && String(row.estimateId || row._id) === estimateId) {
      return row;
    }
  }
  return selected;
}

export { formatGlOptionLabel } from '../../../utils/accrualsFormat';

export function formatEstimateMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  const rounded = Math.round(amount);
  return rounded.toLocaleString('en-US', { maximumFractionDigits: 0, minimumFractionDigits: 0 });
}

function lineSelectedAmountSource(row) {
  return row?.amountSource || row?.raw?.preparer_amount_source || null;
}

function linePostedAmount(row) {
  const amount = row?.preparerAmount
    ?? row?.raw?.preparer_amount
    ?? row?.postAmount
    ?? row?.raw?.final_accrual_amount;
  if (amount == null || amount === '') return null;
  const n = Number(amount);
  return Number.isNaN(n) ? null : n;
}

function sumChildrenBySource(rows, source) {
  let total = 0;
  let hasValue = false;
  (rows || []).forEach((row) => {
    if (lineSelectedAmountSource(row) !== source) return;
    const amount = linePostedAmount(row);
    if (amount == null) return;
    hasValue = true;
    total += amount;
  });
  return hasValue ? total : null;
}

function approvedPoChildren(group) {
  return (group?.children || []).filter((row) => (
    row
    && !isVendorLevelEstimateRow(row)
    && workbenchColumnStatusKey(row) === ACCRUALS_ESTIMATE_STATUS.APPROVED
  ));
}

export function parentFinalAmountSplitLabel(group) {
  const rows = approvedPoChildren(group);
  if (!rows.length) return '';
  const rule = sumChildrenBySource(rows, ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE);
  const noa = sumChildrenBySource(rows, ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA);
  const custom = sumChildrenBySource(rows, ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM);
  const parts = [];
  if (rule != null) parts.push(`Rule: ${formatEstimateMoney(rule)}`);
  if (noa != null) parts.push(`Noa Estimate: ${formatEstimateMoney(noa)}`);
  if (custom != null) parts.push(`Custom: ${formatEstimateMoney(custom)}`);
  return parts.join(' • ');
}

export function visibleParentFinalAmount(group) {
  const rows = approvedPoChildren(group);
  let total = 0;
  let hasValue = false;
  rows.forEach((row) => {
    const amount = linePostedAmount(row);
    if (amount == null || Number.isNaN(Number(amount))) return;
    hasValue = true;
    total += Number(amount);
  });
  return hasValue ? total : null;
}

export function estimateLineRecordAmount(line) {
  if (!line) return null;
  if (line.preparerAmount != null && line.preparerAmount !== '') return Number(line.preparerAmount);
  if (line.amount != null && line.amount !== '') return Number(line.amount);
  if (line.noaEstimate != null && line.noaEstimate !== '') return Number(line.noaEstimate);
  if (line.ruleEstimate != null && line.ruleEstimate !== '') return Number(line.ruleEstimate);
  return null;
}

export function parseEstimateMoney(value) {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isNaN(amount) ? null : amount;
}

export function defaultEstimateSource(group) {
  const saved = group?.vendorRuleRow?.raw?.preparer_amount_source || group?.raw?.preparer_amount_source;
  if (saved === ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE
    || saved === ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA
    || saved === ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM) {
    return saved;
  }
  if (group?.noaEstimate != null && group.noaEstimate !== '') {
    return ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA;
  }
  if (group?.ruleEstimate != null && group.ruleEstimate !== '') {
    return ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE;
  }
  return ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM;
}

export function estimateSourceOptions(group) {
  const options = [];
  const children = group?.children || [];
  const hasRule = (group?.ruleEstimate != null && group.ruleEstimate !== '')
    || children.some((row) => row.ruleEstimate != null && row.ruleEstimate !== '');
  const hasNoa = (group?.noaEstimate != null && group.noaEstimate !== '')
    || children.some((row) => isPoIrPending(row) || isPoNoaReady(row));
  if (hasRule) {
    options.push({
      id: ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE,
      label: ACCRUALS_ESTIMATE_SOURCE_LABELS[ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE],
    });
  }
  if (hasNoa) {
    options.push({
      id: ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA,
      label: ACCRUALS_ESTIMATE_SOURCE_LABELS[ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA],
    });
  }
  options.push({
    id: ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM,
    label: ACCRUALS_ESTIMATE_SOURCE_LABELS[ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM],
  });
  return options;
}

export const VENDOR_REF_VISIBLE_CHIP_COUNT = 1;

export function buildVendorRefTags(group) {
  const tags = [];
  const supplierId = String(group?.supplierId || '').trim();
  if (supplierId && supplierId !== 'unknown') {
    tags.push({
      type: 'vendor',
      label: /^V-/i.test(supplierId) ? supplierId : `V-${supplierId}`,
    });
  }
  const seen = new Set();
  (group?.children || []).forEach((child) => {
    const po = String(child?.poNumber || '').trim();
    if (!po || seen.has(po)) return;
    seen.add(po);
    tags.push({ type: 'po', label: po });
  });
  return tags;
}

export function visibleVendorRefTags(tags) {
  return (tags || []).slice(0, VENDOR_REF_VISIBLE_CHIP_COUNT);
}

export function overflowVendorRefTags(tags) {
  return (tags || []).slice(VENDOR_REF_VISIBLE_CHIP_COUNT);
}

export function vendorGroupGlLabels(group) {
  const seen = new Set();
  const labels = [];
  (group?.children || []).forEach((child) => {
    const debit = String(child?.debitGl || child?.raw?.debit_gl || '').trim();
    const credit = String(child?.creditGl || child?.raw?.credit_gl || '').trim();
    const key = [debit, credit].filter(Boolean).join(' / ');
    if (!key || seen.has(key)) return;
    seen.add(key);
    labels.push(key);
  });
  return labels;
}

export function vendorSeeMoreTooltipLines(group, overflowTags) {
  const poLines = (group?.refTags || [])
    .filter((tag) => tag.type === 'po')
    .map((tag) => tag.label)
    .filter(Boolean);
  const lines = poLines.length
    ? poLines
    : (overflowTags || []).map((tag) => tag.label).filter(Boolean);
  vendorGroupGlLabels(group).forEach((label) => {
    if (!lines.includes(label)) lines.push(label);
  });
  return lines;
}

export function groupEstimatedAccrual(group) {
  let openTotal = 0;
  let hasOpen = false;
  (group?.children || []).forEach((child) => {
    const value = child.openPoValue != null ? child.openPoValue : child.raw?.open_po_value;
    if (value == null || value === '') return;
    hasOpen = true;
    openTotal += Number(value) || 0;
  });
  if (hasOpen) return openTotal;
  if (group?.noaEstimate != null) return group.noaEstimate;
  return group?.ruleEstimate ?? group?.amount ?? null;
}

export function noaPoLines(group) {
  return (group?.children || [])
    .filter((row) => !isVendorLevelEstimateRow(row) && (
      Boolean(row.poNumber)
      || row.noaEstimate != null
      || Boolean(row.informationRequestId)
      || (
        row.infoRequest
        && row.infoRequest !== ACCRUALS_ESTIMATE_INFO_REQUEST.NONE
      )
    ))
    .sort((a, b) => {
      const rank = { ready: 0, idle: 1, ir: 2 };
      const aRank = rank[poNoaDisplayState(a)] ?? 1;
      const bRank = rank[poNoaDisplayState(b)] ?? 1;
      if (aRank !== bRank) return aRank - bRank;
      return String(a.poNumber || '').localeCompare(String(b.poNumber || ''));
    });
}

export function poRuleEstimateLines(group) {
  return (group?.children || []).filter(isPoRuleEstimate);
}

export function irPendingPoLines(group) {
  return (group?.children || []).filter(isPoIrPending);
}

export function noaReadyPoLines(group) {
  return (group?.children || []).filter(isPoNoaReady);
}

export function estimateLineDescription(row) {
  return row?.description
    || row?.raw?.description
    || row?.raw?.item_description
    || row?.raw?.po_description
    || row?.detail?.noaQuote
    || '—';
}

export function expandLineNoaDescription(line) {
  if (!line || isPoIrPending(line)) return '';
  const raw = line.raw || {};
  const quote = String(line.detail?.noaQuote || '').trim();
  if (quote && quote !== '—' && !quote.toLowerCase().startsWith('method:')) return quote;
  const reasoning = String(line.detail?.noaReasoning || '').trim();
  if (reasoning && reasoning !== '—') return reasoning;
  const fromRaw = String(
    raw.im_response
    || raw.signals?.text?.reasoning
    || raw.ai_reasoning
    || raw.reasoning
    || '',
  ).trim();
  return fromRaw;
}

export function expandLineDescriptionChips(line) {
  const chips = [];
  if (lineHasRuleSource(line)) {
    const section = buildRuleEstimateSection(line?.raw || {}, line || {});
    const label = String(
      formatWinningRuleLabel(line?.raw || {}, line || {})
      || section?.subtitle
      || line?.rule
      || '',
    ).trim();
    if (label) chips.push({ id: 'rule', kind: 'rule', label });
  }
  const hasNoaEstimate = line?.noaEstimate != null && line.noaEstimate !== '';
  let noaLabel = expandLineNoaDescription(line);
  if (!noaLabel && (hasNoaEstimate || line?.infoRequest === ACCRUALS_ESTIMATE_INFO_REQUEST.REPLIED)) {
    const item = String(
      line?.description
      || line?.raw?.item_description
      || line?.raw?.po_description
      || '',
    ).trim();
    if (item && item !== '—' && item !== chips[0]?.label) noaLabel = item;
  }
  if ((hasNoaEstimate || line?.infoRequest === ACCRUALS_ESTIMATE_INFO_REQUEST.REPLIED) && noaLabel) {
    chips.push({ id: 'noa', kind: 'noa', label: noaLabel });
  }
  return chips;
}

export function estimateConfirmAmount(group, source, customAmount) {
  if (source === ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM) {
    return parseEstimateMoney(customAmount);
  }
  if (source === ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA) {
    const ready = noaReadyPoLines(group);
    if (ready.length) {
      return ready.reduce((sum, row) => sum + (Number(row.noaEstimate) || 0), 0);
    }
    return group?.noaEstimate != null ? Number(group.noaEstimate) : null;
  }
  if (group?.vendorRuleRow?.ruleEstimate != null) return Number(group.vendorRuleRow.ruleEstimate);
  if (group?.ruleEstimate != null) return Number(group.ruleEstimate);
  return group?.amount != null ? Number(group.amount) : null;
}

export function formatEstimateRelativeTime(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

export function estimateIrBannerSubtitle(group) {
  const email = group?.imOwnerEmail || group?.detail?.irFromEmail;
  const relative = formatEstimateRelativeTime(group?.imSentAt);
  const parts = [];
  if (email && email !== '—') parts.push(`Sent to ${email}`);
  if (relative) parts.push(relative);
  parts.push('awaiting response');
  return parts.join(' · ');
}

export function expandWorkbenchLines(group) {
  const lines = [];
  const seen = new Set();
  const pushLine = (row) => {
    if (!row) return;
    const id = String(row._id || row.lineKey || row.poNumber || '');
    if (id && seen.has(id)) return;
    if (id) seen.add(id);
    lines.push(row);
  };
  pushLine(group?.vendorRuleRow);
  (group?.children || []).forEach((row) => {
    if (isVendorLevelEstimateRow(row)) return;
    pushLine(row);
  });
  return lines;
}

export function lineHasRuleSource(line) {
  if (!line) return false;
  return line.ruleEstimate != null && line.ruleEstimate !== '';
}

export function lineHasNoaSource(line) {
  if (!line || isPoIrPending(line)) return false;
  if (line.noaEstimate != null && line.noaEstimate !== '') return true;
  return line.infoRequest === ACCRUALS_ESTIMATE_INFO_REQUEST.REPLIED;
}

export function expandLineSourceOptions(line) {
  const irPending = isPoIrPending(line) || line?.noaStatus === 'ir_pending';
  const options = [];
  if (lineHasRuleSource(line)) {
    options.push({
      id: ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE,
      label: ACCRUALS_ESTIMATE_SOURCE_LABELS.rule,
    });
  }
  if (lineHasNoaSource(line) || irPending) {
    options.push({
      id: ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA,
      label: ACCRUALS_ESTIMATE_SOURCE_LABELS.noa,
      sparkle: true,
      disabled: irPending,
    });
  }
  options.push({
    id: ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM,
    label: ACCRUALS_ESTIMATE_SOURCE_LABELS.custom,
  });
  return options;
}

export function defaultExpandLineSource(line, preferredSource) {
  const saved = line?.amountSource || line?.raw?.preparer_amount_source;
  const allowed = expandLineSourceOptions(line)
    .filter((option) => !option.disabled)
    .map((option) => option.id);
  if (allowed.includes(saved)) return saved;
  if (allowed.includes(preferredSource)) return preferredSource;
  if (allowed.includes(ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE)) {
    return ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE;
  }
  if (allowed.includes(ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA)) {
    return ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA;
  }
  return ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM;
}

export function expandLineSavedCustomAmount(line) {
  const source = line?.amountSource || line?.raw?.preparer_amount_source;
  if (source !== ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM) return null;
  const amount = line?.preparerAmount
    ?? line?.raw?.preparer_amount
    ?? line?.postAmount
    ?? line?.raw?.final_accrual_amount;
  if (amount == null || amount === '') return null;
  return amount;
}

export function expandLineCustomSeedAmount(line) {
  const saved = expandLineSavedCustomAmount(line);
  if (saved != null) return saved;
  if (line?.ruleEstimate != null && line.ruleEstimate !== '') return line.ruleEstimate;
  if (line?.noaEstimate != null && line.noaEstimate !== '') return line.noaEstimate;
  return null;
}

export function expandLineActiveAmount(line, source, customAmount) {
  if (source === ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM) {
    const typed = parseEstimateMoney(customAmount);
    if (typed != null) return typed;
    const saved = expandLineSavedCustomAmount(line);
    return saved != null ? Number(saved) : null;
  }
  if (source === ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA) {
    return line?.noaEstimate != null && line.noaEstimate !== '' ? Number(line.noaEstimate) : null;
  }
  return line?.ruleEstimate != null && line.ruleEstimate !== '' ? Number(line.ruleEstimate) : null;
}

export function vendorReferenceLabel(group, line) {
  if (line?.poNumber) return line.poNumber;
  const supplierId = String(group?.supplierId || line?.supplierId || '').trim();
  if (supplierId && supplierId !== 'unknown') {
    return /^V-/i.test(supplierId) ? supplierId : `V-${supplierId}`;
  }
  return 'Vendor';
}

export function estimateConfirmSourceLabel(group) {
  const source = group?.vendorRuleRow?.raw?.preparer_amount_source || null;
  const code = group?.vendorRuleRow?.raw?.rule_template_code || group?.rule || 'rule';
  if (source === ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA) return 'Noa';
  if (source === ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM) return 'Custom';
  return code;
}

export function buildEstimatesFilterCounts(groups = []) {
  const counts = {
    all: 0,
    [ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION]: 0,
    [ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL]: 0,
    [ACCRUALS_ESTIMATE_STATUS.APPROVED]: 0,
    [ACCRUALS_ESTIMATE_STATUS.REJECTED]: 0,
    [ACCRUALS_ESTIMATE_STATUS.EXCLUDED]: 0,
  };
  (groups || []).forEach((group) => {
    const records = (group?.children || []).filter((row) => row && !isVendorLevelEstimateRow(row));
    records.forEach((row) => {
      counts.all += 1;
      const status = workbenchColumnStatusKey(row);
      if (counts[status] != null) counts[status] += 1;
    });
  });
  return counts;
}

